import React, { useState } from 'react';
import { Table, Badge, Button, Modal, Row, Col, Card, Spinner, Alert, Tabs, Tab } from 'react-bootstrap';
import { useLoyaltySettlements } from '../hooks/use-loyalty-settlements';
import { formatCurrency, formatDateDayMonthYear } from '../../../utils';
import OrdersModalButton from '../../Orders/OrdersModalButton';

const LoyaltySettlementsTable = () => {
  const {
    settlements,
    totalsByPeriod,
    totalsByCurrency,
    isLoading
  } = useLoyaltySettlements();
  const [showModal, setShowModal] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState<any>(null);

  const handleShowDetails = (settlement: any) => {
    setSelectedSettlement(settlement);
    setShowModal(true);
  };

  const getStatusBadge = (status: string, paidStatus: boolean | string) => {
    if (!paidStatus || paidStatus === 'false') {
      return <Badge bg="warning">⏳ Pendiente</Badge>;
    } else if (paidStatus === 'SUCCESS') {
      return <Badge bg="success">✅ Pagado</Badge>;
    } else if (paidStatus === 'PENDING') {
      return <Badge bg="warning">⏳ Pendiente</Badge>;
    } else if (paidStatus === 'PROCESSING') {
      return <Badge bg="info">Procesando</Badge>;
    } else {
      const rejectionReason = paidStatus === 'REJECTED_BY_MONNET'
        ? 'Monnet'
        : paidStatus === 'REJECTED_BY_ACCOUNT'
          ? 'No tener cuenta'
          : 'No cumplir los valores';
      return <Badge bg="danger">Rechazado por ({rejectionReason})</Badge>;
    }
  };



  const formatPeriod = (period: { start: string; end: string }) => {
    return `${formatDateDayMonthYear(period.start)} - ${formatDateDayMonthYear(period.end)}`;
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando liquidaciones...</span>
        </Spinner>
      </div>
    );
  }


  return (
    <>
      <Card>
        <Card.Header>
          <h3 className="mb-0">Historial de Liquidaciones - Regalías</h3>
          <div className="mt-2">
            {Object.keys(totalsByCurrency).length > 0 ? (
              <div className="d-flex flex-wrap gap-3">
                {Object.entries(totalsByCurrency).map(([currency, data]) => (
                  <div key={currency} className="text-muted">
                    <strong>{currency}:</strong> {formatCurrency(data.value, currency)}
                    <small className="ms-1">({data.points.toLocaleString()} pts)</small>
                  </div>
                ))}
              </div>
            ) : (
              <small className="text-muted">No hay liquidaciones registradas</small>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {settlements.length === 0 ? (
            <Alert variant="info">
              No tienes liquidaciones de regalías registradas aún.
            </Alert>
          ) : (
            <div className="default-table-area">
              <div className="table-responsive">
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Período</th>
                      <th>Total Puntos</th>
                      <th>Valor Calculado</th>
                      <th>Estado</th>
                      <th>Fecha Creación</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {totalsByPeriod.map((settlement: any, index: number) => (
                      <tr key={settlement.settlement_id || index}>
                        <td>{formatPeriod(settlement.period)}</td>
                        <td>{settlement.total_points.toLocaleString()}</td>
                        <td>
                          {Object.keys(settlement.currencies_breakdown).length > 1 ? (
                            <div>
                              {Object.entries(settlement.currencies_breakdown).map(([currency, data]: [string, any]) => (
                                <div key={currency} className="small">
                                  <strong>{currency}:</strong> {formatCurrency(data.value, currency)}
                                </div>
                              ))}
                            </div>
                          ) : (
                            formatCurrency(settlement.total_value, Object.keys(settlement.currencies_breakdown)[0])
                          )}
                        </td>
                        <td>{getStatusBadge(settlement.status, settlement.paid_status)}</td>
                        <td>{formatDateDayMonthYear(settlement.created_at)}</td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleShowDetails(settlement)}
                          >
                            Ver Detalles
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>

          )}
        </Card.Body>
      </Card>

      {/* Modal de Detalles */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Detalles de Liquidación - Regalías</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSettlement && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Período:</strong> {formatPeriod(selectedSettlement.period)}
                </Col>
                <Col md={6}>
                  <strong>Estado:</strong> {getStatusBadge(selectedSettlement.status, selectedSettlement.paid_status)}
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Fecha de Cálculo:</strong> {formatDateDayMonthYear(selectedSettlement.calculation_date)}
                </Col>
                <Col md={6}>
                  <strong>Fecha de Creación:</strong> {formatDateDayMonthYear(selectedSettlement.created_at)}
                </Col>
              </Row>

              <hr />

              <h6 className="mb-3">Detalles por Moneda</h6>
              {selectedSettlement.user_data?.total_by_currency && Object.keys(selectedSettlement.user_data.total_by_currency).length > 0 ? (
                Object.keys(selectedSettlement.user_data.total_by_currency).length === 1 ? (
                  // Una sola moneda - mostrar directamente sin tabs
                  Object.entries(selectedSettlement.user_data.total_by_currency).map(([currency, data]: [string, any]) => (
                    <Card key={currency} className="mb-3">
                      <Card.Header>
                        <h6 className="mb-0">
                          {currency} - Total: {formatCurrency(data.total_value, currency)}
                        </h6>
                        <small className="text-muted">
                          Puntos: {data.total_points?.toLocaleString() || 0} |
                          Valor por punto: {formatCurrency(data.point_value, currency)}
                        </small>
                      </Card.Header>
                      <Card.Body>
                        <Row>
                          <Col md={4}>
                            <div className="text-center p-3 bg-success bg-opacity-10 rounded">
                              <h5 className="mb-1">{data.total_points?.toLocaleString() || 0}</h5>
                              <small className="text-muted">Total Puntos</small>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="text-center p-3 bg-success bg-opacity-10 rounded">
                              <h5 className="mb-1">{formatCurrency(data.point_value, currency)}</h5>
                              <small className="text-muted">Valor por Punto</small>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="text-center p-3 bg-success bg-opacity-10 rounded">
                              <h5 className="mb-1">{formatCurrency(data.total_value, currency)}</h5>
                              <small className="text-muted">Total Calculado</small>
                            </div>
                          </Col>
                        </Row>
                        {/* Mostrar detalles por niveles si están disponibles */}
                        {selectedSettlement.user_data?.currencies?.[currency]?.levels && selectedSettlement.user_data.currencies[currency].levels.length > 0 && (
                          <div className="mt-4">
                            <h6>Detalles por Niveles</h6>
                            <div className='default-table-area'>
                              <div className="table-responsive mt-3">
                                <Table size="sm" striped>
                                  <thead>
                                    <tr>
                                      <th>Nivel</th>
                                      <th>% Aplicado</th>
                                      <th>Puntos</th>
                                      <th>Valor Calculado</th>
                                      <th>Órdenes</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {selectedSettlement.user_data.currencies[currency].levels.map((level: any, levelIdx: number) => (
                                      <tr key={levelIdx}>
                                        <td>{level.level}</td>
                                        <td>{level.percentage}%</td>
                                        <td>{level.points?.toLocaleString() || 0}</td>
                                        <td>{formatCurrency(level.value, currency)}</td>
                                        <td>
                                          <OrdersModalButton
                                            orderIds={level.orders || []}
                                            currency={currency}
                                            period={selectedSettlement.period}
                                            settlementType="LOYALTY_ROYALTY"
                                            variant="outline-primary"
                                            size="sm"
                                          >
                                            Ver Órdenes
                                          </OrdersModalButton>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            </div>

                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  ))
                ) : (
                  // Múltiples monedas - usar tabs
                  <Tabs defaultActiveKey={Object.keys(selectedSettlement.user_data.total_by_currency)[0]} className="mb-3">
                    {Object.entries(selectedSettlement.user_data.total_by_currency).map(([currency, data]: [string, any]) => (
                      <Tab eventKey={currency} title={`${currency} - ${formatCurrency(data.total_value, currency)}`} key={currency}>
                        <Card className="mt-3">
                          <Card.Header>
                            <h6 className="mb-0">
                              Resumen {currency}
                            </h6>
                            <small className="text-muted">
                              Puntos: {data.total_points?.toLocaleString() || 0} |
                              Valor por punto: {formatCurrency(data.point_value, currency)}
                            </small>
                          </Card.Header>
                          <Card.Body>
                            <Row>
                              <Col md={4}>
                                <div className="text-center p-3 bg-success bg-opacity-10 rounded">
                                  <h5 className="mb-1">{data.total_points?.toLocaleString() || 0}</h5>
                                  <small className="text-muted">Total Puntos</small>
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="text-center p-3 bg-success bg-opacity-10 rounded">
                                  <h5 className="mb-1">{formatCurrency(data.point_value, currency)}</h5>
                                  <small className="text-muted">Valor por Punto</small>
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="text-center p-3 bg-success bg-opacity-10 rounded">
                                  <h5 className="mb-1">{formatCurrency(data.total_value, currency)}</h5>
                                  <small className="text-muted">Total Calculado</small>
                                </div>
                              </Col>
                            </Row>
                            {/* Mostrar detalles por niveles si están disponibles */}
                            {selectedSettlement.user_data?.currencies?.[currency]?.levels_breakdown && selectedSettlement.user_data.currencies[currency].levels_breakdown.length > 0 && (
                              <div className="mt-4">
                                <h6>Detalles por Niveles</h6>
                                <div className="default-table-area">
                                  <div className="table-responsive mt-3">
                                    <Table size="sm" striped>
                                      <thead>
                                        <tr>
                                          <th>Nivel</th>
                                          <th>% Aplicado</th>
                                          <th>Puntos</th>
                                          <th>Valor Calculado</th>
                                          <th>Órdenes</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {selectedSettlement.user_data.currencies[currency].levels_breakdown.map((level: any, levelIdx: number) => (
                                          <tr key={levelIdx}>
                                            <td>{level.level}</td>
                                            <td>{level.percentage}%</td>
                                            <td>{level.points?.toLocaleString() || 0}</td>
                                            <td>{formatCurrency(level.value, currency)}</td>
                                            <td>
                                              <OrdersModalButton
                                                orderIds={level.orders || []}
                                                currency={currency}
                                                period={selectedSettlement.period}
                                                settlementType="LOYALTY_ROYALTY"
                                                variant="outline-primary"
                                                size="sm"
                                              >
                                                Ver Órdenes
                                              </OrdersModalButton>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  </div>
                                </div>

                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      </Tab>
                    ))}
                  </Tabs>
                )
              ) : (
                <Alert variant="info">
                  No hay detalles de monedas para esta liquidación.
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LoyaltySettlementsTable;
